// Which base scores this model: decision trees or rules. Behind
// studio-tree-insights; flag off renders `ModelBaseChoicePage` unchanged.
//
// Switching changes the scale the score is on (a smoothed 0-100 blend for trees,
// the raw rule total for rules) and deliberately does not touch the thresholds,
// so that switching and switching straight back loses nothing. The price is
// that right after a switch the thresholds were set for the other scale, and
// this is the one moment the page knows it: so it says so, here, with the way
// to fix it.
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';

import ModelBaseNames from '../../../models/modelBase/ModelBaseNames';
import Store from '../../../store';
import Error from '../../components/error/Error';
import './modelBasePage.css';

const OPTIONS: {
  base: ModelBaseNames;
  title: string;
  summary: string;
  scale: string;
}[] = [
  {
    base: ModelBaseNames.trees,
    title: 'Decision trees',
    summary: 'Learned from historical conversions in the training set.',
    scale: 'Scores 0 to 100',
  },
  {
    base: ModelBaseNames.points,
    title: 'Rules',
    summary: 'Points you assign, added up per lead.',
    scale: 'Scores the point total',
  },
];

const NAME: Record<string, string> = {
  [ModelBaseNames.trees]: 'decision trees',
  [ModelBaseNames.points]: 'rules',
};

const ModelBasePage = inject('store')(
  observer(
    class ModelBasePage extends React.Component<
      { store?: Store },
      { switchedFrom: ModelBaseNames | null; error: string | null }
    > {
      constructor(props: { store?: Store }) {
        super(props);
        this.state = { switchedFrom: null, error: null };
      }

      async choose(base: ModelBaseNames) {
        const { store } = this.props;
        const from = store.activeModel.base;
        if (base === from || store.modelBasePage.isSaving) return;
        try {
          await store.saveModelBase(base);
          // Back to where the thresholds were set clears the notice rather than
          // stating the opposite one.
          this.setState({
            switchedFrom: this.state.switchedFrom === base ? null : from,
            error: null,
          });
        } catch {
          this.setState({ error: 'The model base could not be changed.' });
        }
      }

      render() {
        const { activeModel, isAllowedToEdit, modelBasePage, tenant, modelId } =
          this.props.store;
        const { switchedFrom, error } = this.state;
        return (
          <div className="mb mt-3 mb-5">
            <h5 className="mb-title">Model base</h5>
            <div
              className="mb-options"
              role="radiogroup"
              aria-label="Model base"
            >
              {OPTIONS.map((option) => {
                const current = activeModel.base === option.base;
                return (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={current}
                    key={option.base}
                    data-testid={`model-base-${option.base}`}
                    className={
                      current ? 'mb-option mb-option--on' : 'mb-option'
                    }
                    disabled={!isAllowedToEdit || modelBasePage.isSaving}
                    onClick={() => this.choose(option.base)}
                  >
                    <span className="mb-option-head">
                      <span className="mb-option-title">{option.title}</span>
                      {current && <span className="mb-option-tag">In use</span>}
                    </span>
                    <span className="mb-option-summary">{option.summary}</span>
                    <span className="mb-option-scale">{option.scale}</span>
                  </button>
                );
              })}
            </div>

            {error && <Error message={error} />}

            {switchedFrom && (
              <p className="mb-notice" data-testid="model-base-notice">
                Now scored by {NAME[activeModel.base]}. The thresholds are as
                they were set for {NAME[switchedFrom]}, on the other scale.{' '}
                <Link to={`/tenant/${tenant}/models/${modelId}/model/tuning`}>
                  Review them on Fine tuning
                </Link>
              </p>
            )}
          </div>
        );
      }
    }
  )
);

export default ModelBasePage;
